import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Popconfirm, Space, Table, Tooltip, Typography } from 'antd';
import type { TableColumnsType as ColumnsType } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import { useModal } from 'services';
import { listBlueprints, deleteBlueprint, selectors, Blueprint } from 'state';
import { columnSearchWidget, LoadFailure } from 'components';
import CreateBlueprintModal from './CreateBlueprintModal';

const BlueprintsList: React.FC = () => {
  const dispatch = useDispatch();
  const blueprints = useSelector(selectors.blueprintsList);
  const waiting = useSelector(selectors.blueprintsWaiting);
  const error = useSelector(selectors.blueprintsError);
  const [CreateModal, showCreateModal] = useModal(CreateBlueprintModal);

  useEffect(() => {
    dispatch(listBlueprints({ summary_scope: 'none' }));
  }, [dispatch]);

  const columns: ColumnsType<Blueprint> = [
    {
      title: 'Id',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      sorter: (a, b) => a.id - b.id,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0),
      ...columnSearchWidget({ dataIndex: 'name' }),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
    },
    {
      title: 'Versions',
      key: 'versions',
      width: 90,
      render: (_v, record) => record.versions.length,
    },
    {
      title: 'Difficulties',
      key: 'difficulties',
      width: 110,
      render: (_v, record) => record.difficulties.length,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: function Actions(_v, record) {
        // locked_at (is_locked) is set on the first launch, so any locked version means the blueprint has
        // been launched from — deletion is blocked (server-side too) to preserve launch provenance.
        const launchedFrom = record.versions.some(v => v.is_locked);
        return (
          <Space size="small">
            <Link to={`/dashboard/blueprints/${record.id}`}>
              <Button size="small">Open</Button>
            </Link>
            {launchedFrom ? (
              <Tooltip title="This blueprint has been launched from and can't be deleted — its launch history links existing sessions back to the scenario.">
                <Button size="small" danger disabled>
                  Delete
                </Button>
              </Tooltip>
            ) : (
              <Popconfirm
                title="Delete this blueprint and all its templates?"
                onConfirm={() => dispatch(deleteBlueprint({ id: record.id }))}
              >
                <Button size="small" danger>
                  Delete
                </Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <Space
        size={15}
        style={{
          marginBottom: 20,
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Typography.Title level={4} style={{ margin: 0 }}>
          Session Blueprints
        </Typography.Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => showCreateModal({})}
        >
          Create blueprint
        </Button>
      </Space>
      {error ? (
        <LoadFailure
          subject="Scenarios"
          onRetry={() => dispatch(listBlueprints({ summary_scope: 'none' }))}
        />
      ) : (
        <Table
          rowKey="id"
          loading={waiting}
          dataSource={blueprints}
          columns={columns}
          bordered
          pagination={{ defaultPageSize: 20 }}
        />
      )}
      {CreateModal}
    </div>
  );
};

export default BlueprintsList;
